import { useState } from 'react'
import { saveReturnData } from '@api/returns'
import { type Return, type ReturnsProduct } from '@api/returns/types'
import { scrollToTop, getValueFromObject, objectKeys } from '@helpers/utils'
import trackError from '@helpers/trackError'
import useSessionStorage from '@helpers/hooks/useSessionStorage'
import Translations from '@helpers/translations'
import {
  commonEventProductProperties,
  getProductCategory,
  getProductVariantAsString
} from '@helpers/products'
import Notification, {
  type NotificationProps
} from '@components/ui/Notification'
import PageLoading from '@components/Loading/PageLoading'
import { type SubmitButtonHandlers } from '@components/ui/Button/SubmitButton'
import { type EmptyObject } from 'types/helpers'
import { returnsAnalytics } from '../helpers'
import { type Label, type Method } from '../types'
import useFetchOrderData, { OrderData } from './hooks/useFetchOrderData'
import ReturnsBanner from '../ReturnsBanner'
import ReturnsFormModal, { type ReturnData } from './ReturnsFormModal'
import ReturnsNoteModal from './ReturnsNoteModal'
import ReturnsContent from './ReturnsContent'
import ReturnsFormHeader from './ReturnsFormHeader'
import useReturnCompletedUpdateFitModal from './hooks/useReturnCompletedUpdateFitModal'
import ReturnsSavedFitConfirmationModal from './ReturnsSavedFitConfirmationModal'
import styles from './styles.module.scss'
import { type SetProductToReturn } from './ReturnsContent/ProductCardFooter'

function getNumberOfReturns(method: Method, products: ReturnsProduct[] = []) {
  return products.filter(
    product => product.return && product.return.method === method
  ).length
}

const getReturnsCopy = ({
  numberExchanges,
  numberRefunds
}: {
  numberExchanges: number
  numberRefunds: number
}) => {
  const exchanges =
    numberExchanges === 0
      ? ''
      : `${numberExchanges} ${
          numberExchanges > 1
            ? Translations.localise('pages.returns.exchanges-lc')
            : Translations.localise('pages.returns.exchange-lc')
        }`
  const refunds =
    numberRefunds === 0
      ? ''
      : `${numberRefunds} ${
          numberRefunds > 1
            ? Translations.localise('pages.returns.refunds-lc')
            : Translations.localise('pages.returns.refund-lc')
        }`

  switch (true) {
    case exchanges !== '' && refunds !== '':
      return [exchanges, refunds].join('. ')

    case exchanges !== '':
      return exchanges

    case refunds !== '':
      return refunds

    default:
      return ''
  }
}

function commonReturnsProductEventProperties(product: ReturnsProduct) {
  return {
    ...commonEventProductProperties(product),
    category: getProductCategory(
      product.exchange_options,
      product.product_type
    ),
    sku: product.sku,
    variant: getProductVariantAsString(product.sizes),
    price: Number(product.full_price),
    url: window.location.href
  }
}

function productReturnEvent(orderData: OrderData, querySuccessReturn: string) {
  if (!orderData?.returns || !querySuccessReturn) {
    return null
  }

  orderData?.returns[querySuccessReturn].products.forEach(product => {
    if (product.return) {
      const exchangeSizes: Return['sizes'] = {}
      Object.entries(product.sizes).forEach(([sizeKey, sizeValue]) => {
        const exchangedSize = getValueFromObject<Return['sizes'] | EmptyObject>(
          product,
          'return.sizes',
          {}
        )[sizeKey as keyof Return['sizes']]
        exchangeSizes[sizeKey as keyof Return['sizes']] =
          exchangedSize !== sizeValue ? exchangedSize : sizeValue
      })
      const returnReason =
        (
          product.return_reasons.find(
            reason => reason.id === Number(product?.return?.reason)
          ) || {}
        ).title || ''
      const returnMethod = product.return.method

      const productData = {
        ...commonReturnsProductEventProperties(product),
        return_type: returnMethod,
        return_reason: returnReason,
        ...(returnMethod === 'exchange' && {
          exchange_color: product.return.color?.title || null,
          exchange_variant: getProductVariantAsString(exchangeSizes)
        })
      }
      returnsAnalytics('Product Return Created', productData)
    }
  })
}

function productReturnSavedEvent(
  orderData: Partial<OrderData>,
  values: Return,
  id: string
) {
  const productToReturn = orderData?.products?.find(
    product => product.id === id
  )
  const returnReason =
    (
      productToReturn?.return_reasons.find(
        reason => reason.id === Number(values.reason)
      ) || {}
    ).title || ''
  const { method } = values
  if (productToReturn) {
    returnsAnalytics('Product Return Saved', {
      ...commonReturnsProductEventProperties(productToReturn),
      return_type: method,
      return_reason: returnReason
    })
  }
}

const DEFAULT_STATE = { method: '' as Method, product: '', step: 0 }

type ItemsSelectionProps = {
  orderNumber: string | undefined
  postcode: string | undefined
  successReturn: string | undefined
  label: Label
  onSubmit: (args: {
    postcode: string
    order_number: string
    return_id: string
  }) => void
}

export type CloseReturnsFormModalProps = (args?: {
  id: string
  values: ReturnData
}) => void

function ItemsSelection({
  orderNumber = '',
  postcode = '',
  successReturn: querySuccessReturn = '',
  onSubmit,
  label = {}
}: ItemsSelectionProps) {
  const [selectedProduct, setSelectedProduct] = useState(DEFAULT_STATE)
  const [orderData, setOrderData] = useState<Partial<OrderData>>({})
  const [notificationMessage, setNotificationMessage] = useState<
    | {
        type: NotificationProps['type']
        content: NotificationProps['content']
      }
    | EmptyObject
  >({})

  const {
    shouldShowReturnCompletedUpdateFitModal,
    setShowReturnCompletedUpdateFitModal
  } = useReturnCompletedUpdateFitModal({
    returns: orderData.returns,
    customerFit: orderData.customerFit,
    querySuccessReturn
  })

  const [hasShownReturnsNoteModal, setHasShownReturnsNoteModal] =
    useSessionStorage('hasShownReturnsNoteModal', false)

  const numberExchanges = getNumberOfReturns('exchange', orderData.products)
  const numberRefunds = getNumberOfReturns('refund', orderData.products)

  const returnRequestCopy = getReturnsCopy({ numberExchanges, numberRefunds })

  const selection = (orderData.products || []).find(
    product => product.id === selectedProduct.product
  )

  const handleOrderData = (fetchedOrderData: OrderData) => {
    if (querySuccessReturn) {
      productReturnEvent(fetchedOrderData, querySuccessReturn)
    } else {
      returnsAnalytics('Return Started', {
        order_id: orderNumber
      })
    }
    setOrderData(fetchedOrderData)
  }

  useFetchOrderData({
    orderNumber,
    postcode,
    onSuccess: handleOrderData,
    setNotificationMessage
  })

  const closeReturnsFormModal = (args?: { id: string; values: Return }) => {
    const { id, values } = args || {}
    if (id && values) {
      const newOrderData = {
        ...orderData,
        products: orderData.products?.map(product =>
          product.id === id ? { ...product, return: values } : product
        )
      }
      setOrderData(newOrderData)
      productReturnSavedEvent(orderData, values, id)
    }
    setSelectedProduct(DEFAULT_STATE)
  }

  const scrollToReturnedProduct = () => {
    if (querySuccessReturn && document.getElementById(querySuccessReturn)) {
      scrollToTop(document.getElementById(querySuccessReturn)?.offsetTop)
    }
  }

  const cancelProductToReturn = (id: string) =>
    setOrderData({
      ...orderData,
      products: orderData?.products?.map(product =>
        product.id === id
          ? (({ return: _omit, ...rest }) => rest)(product) // omit return property to reset the return product to original state
          : product
      )
    })

  const setProductToReturn = (id: string, method: Method, step: number) =>
    setSelectedProduct({ method, product: id, step })

  const saveReturn = ({
    triggerButtonSuccess,
    triggerButtonError,
    triggerButtonReady
  }: SubmitButtonHandlers) => {
    if (orderData?.products) {
      saveReturnData({
        products: orderData.products,
        postcode,
        order_number: orderNumber
      })
        .then(res => {
          if (res.status === 200) {
            triggerButtonSuccess()
            onSubmit({
              postcode,
              order_number: orderNumber,
              return_id: res.data.return_id
            })
          } else {
            triggerButtonReady()
          }
        })
        .catch(saveReturnDataError => {
          triggerButtonError()
          const errorMessage = getValueFromObject(
            saveReturnDataError,
            'response.data.message',
            undefined
          )
          scrollToTop()
          setNotificationMessage({
            type: 'error',
            content: errorMessage || 'Your return could not be issued'
          })
          trackError(new Error('saveReturnData failed'), {
            error: saveReturnDataError
          })
        })
    }
  }

  const shouldShowReturnsNoteModal =
    !hasShownReturnsNoteModal && numberExchanges + numberRefunds > 2

  return (
    <section className={styles.Returns}>
      <ReturnsFormHeader orderData={orderData} />
      <Notification
        type="warning"
        theme="dark"
        content={
          <>
            <p>
              Just a heads-up:{' '}
              <strong>returns logged between 3–10 December</strong> won’t appear
              here due to circumstances beyond our control.
            </p>
            <p>
              You can find all the details in your return confirmation email, or
              our Customer Service team will be happy to assist.
            </p>
            <p>
              Rest assured, your return request hasn’t been lost — we’ve got all
              the information safely in the background.
            </p>
          </>
        }
      />
      {notificationMessage.type && <Notification {...notificationMessage} />}
      {!orderData.products ? (
        <PageLoading />
      ) : (
        <>
          <ReturnsContent
            orderProducts={orderData.products}
            processedReturns={orderData?.returns}
            isExpired={orderData.expired}
            setProductToReturn={setProductToReturn as SetProductToReturn}
            cancelProductToReturn={cancelProductToReturn}
            label={label}
            onError={setNotificationMessage}
          />
          {shouldShowReturnsNoteModal && (
            <ReturnsNoteModal
              onClose={() => setHasShownReturnsNoteModal(true)}
            />
          )}
          {!!selectedProduct.method && selection && (
            <ReturnsFormModal
              onClose={closeReturnsFormModal}
              setProductToReturn={setProductToReturn as SetProductToReturn}
              product={selection}
              method={selectedProduct.method}
              customerFit={orderData?.customerFit}
              returnData={selection?.return}
            />
          )}
          {shouldShowReturnCompletedUpdateFitModal && (
            <ReturnsSavedFitConfirmationModal
              savedFit={orderData.customerFit}
              onClose={() => {
                setShowReturnCompletedUpdateFitModal(false)
                scrollToReturnedProduct()
              }}
            />
          )}
        </>
      )}
      <ReturnsBanner
        active={!!returnRequestCopy && !selectedProduct.method}
        buttonActive={true}
        type="returnsRequest"
        onAction={saveReturn}
        copy={returnRequestCopy}
      />
    </section>
  )
}

export default ItemsSelection
